/**
 * Budget Management Page — tabs for budgets, savings goals, and bills
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { BudgetsTab } from './budgets/BudgetsTab';
import { SavingsGoalsTab } from './budgets/SavingsGoalsTab';
import { BillsRemindersTab } from './budgets/BillsRemindersTab';
import { UnsavedChangesDialog } from './budgets/UnsavedChangesDialog';
import { useBudgetNavigationGuard } from '../hooks/useBudgetNavigationGuard';

type BudgetTab = 'budgets' | 'savings' | 'bills';

function tabFromSearchParam(value: string | null): BudgetTab | null {
  if (value === 'budgets' || value === 'savings' || value === 'bills') return value;
  return null;
}

const TABS: { id: BudgetTab; label: string }[] = [
  { id: 'budgets', label: 'Budgets' },
  { id: 'savings', label: 'Savings Goals' },
  { id: 'bills', label: 'Bills & Reminders' },
];

export function BudgetsPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<BudgetTab>(
    () => tabFromSearchParam(searchParams.get('tab')) ?? 'budgets'
  );

  useEffect(() => {
    const tab = tabFromSearchParam(searchParams.get('tab'));
    if (tab) setActiveTab(tab);
  }, [searchParams]);
  const {
    showNavigationDialog,
    requestNavigation,
    confirmDiscard,
    cancelNavigation,
    setHasUnsavedChanges,
  } = useBudgetNavigationGuard();

  const handleDirtyChange = useCallback(
    (dirty: boolean) => {
      setHasUnsavedChanges(dirty);
    },
    [setHasUnsavedChanges]
  );

  const handleTabChange = (tab: BudgetTab) => {
    if (tab === activeTab) return;
    requestNavigation(() => {
      setHasUnsavedChanges(false);
      setActiveTab(tab);
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Budget Management"
        description="Track your spending limits and financial goals"
        helpSlug="budgets"
      />

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-1 -mb-px" role="tablist" aria-label="Budget management tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-purple-600 text-purple-600 dark:text-purple-400 dark:border-purple-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div role="tabpanel">
        {activeTab === 'budgets' && (
          <BudgetsTab onDirtyChange={handleDirtyChange} />
        )}
        {activeTab === 'savings' && (
          <SavingsGoalsTab onDirtyChange={handleDirtyChange} />
        )}
        {activeTab === 'bills' && (
          <BillsRemindersTab onDirtyChange={handleDirtyChange} />
        )}
      </div>

      <UnsavedChangesDialog
        isOpen={showNavigationDialog}
        onCancel={cancelNavigation}
        onDiscard={confirmDiscard}
      />
    </div>
  );
}
